import React, { useState, useEffect } from 'react';
import { jobNoteService } from '../services/jobNoteService';
import { jobService } from '../services/jobService';
import { invoiceService } from '../services/invoiceService';

const MAX_CHARS = 1000;

const JobCompletedDialog = ({ job, formData, onClose }) => {
  const [noteText, setNoteText] = useState('');
  const [createInvoice, setCreateInvoice] = useState(true);
  const [invoiceExists, setInvoiceExists] = useState(null);
  const [invoiceValue, setInvoiceValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const hasNoValue = !formData.value || parseFloat(formData.value) <= 0;
  const needsValue = createInvoice && hasNoValue;

  useEffect(() => {
    invoiceService.getInvoices().then((res) => {
      const invoices = res.data || [];
      const existing = invoices.find(inv => String(inv.job_id) === String(job.id));
      if (existing) {
        setInvoiceExists(existing);
        setCreateInvoice(false);
      }
    }).catch(() => {});
  }, [job.id]);

  const handleDone = async () => {
    if (needsValue && (!invoiceValue || parseFloat(invoiceValue) <= 0)) {
      setError('Please enter a job value to create an invoice.');
      return;
    }

    setSubmitting(true);
    setError('');

    const actions = [];

    try {
      if (noteText.trim()) {
        await jobNoteService.addNote(job.id, noteText.trim());
        actions.push('note added');
      }

      if (createInvoice) {
        if (hasNoValue && invoiceValue) {
          await jobService.updateJob(job.id, { ...formData, value: parseFloat(invoiceValue) });
        }
        const due = new Date(); due.setDate(due.getDate() + 30);
        await invoiceService.createInvoice({ job_id: job.id, due_date: due.toISOString().slice(0, 10) });
        actions.push('invoice created');
      }

      const parts = actions.map(a => a.charAt(0).toUpperCase() + a.slice(1));
      const suffix = parts.length ? ` ${parts.join(' and ')}.` : '.';
      onClose(`Job completed!${suffix}`);
    } catch (e) {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose('Job updated successfully!');
  };

  const remaining = MAX_CHARS - noteText.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Job Completed</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{job.title}</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Note section */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">
              Add a completion note <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
            </label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="e.g. Work completed as per spec, awaiting sign-off..."
              rows={3}
              maxLength={MAX_CHARS}
              className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm transition-all duration-300 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className={`text-xs text-right ${remaining < 100 ? 'text-amber-500 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}>
              {remaining} characters remaining
            </p>
          </div>

          {/* Invoice section */}
          <div className="border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4 space-y-4">
            {invoiceExists ? (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-5 h-5 mt-0.5 text-amber-500 dark:text-amber-400">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Invoice{' '}
                    <a
                      href="/invoices"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      #{invoiceExists.invoice_number}
                    </a>{' '}
                    already exists
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">A duplicate will not be created.</p>
                </div>
              </div>
            ) : (
              <>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createInvoice}
                    onChange={(e) => setCreateInvoice(e.target.checked)}
                    className="w-4 h-4 accent-blue-500 cursor-pointer"
                  />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Create an invoice for this job</span>
                </label>

                {createInvoice && hasNoValue && (
                  <div className="space-y-2 pl-7">
                    <div className="flex items-start gap-2 text-amber-600 dark:text-amber-400">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                      <p className="text-xs">This job has no value. Please enter a value to create an invoice.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-300">£</span>
                      <input
                        type="number"
                        value={invoiceValue}
                        onChange={(e) => setInvoiceValue(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        className="flex-1 px-3 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                )}

                {createInvoice && !hasNoValue && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 pl-7">
                    An invoice will be created for this job.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 pt-0">
          <button
            type="button"
            onClick={handleSkip}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={handleDone}
            disabled={submitting}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving...' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobCompletedDialog;
